import { createSelector, DefaultProjectorFn, MemoizedSelector } from '@ngrx/store';

import {
  InstallationRecommendation,
  RemoteServiceChartConfig,
  RemoteServiceFeatures,
  RemoteServiceFeaturesChartSeries,
} from '@vi-iot/client';

import { AnnualReportData, AuthSelectors, RemoteServiceDiagnosisEntry, RichDevice } from '@store';

import { AppState } from '../app.state';
import { DevicesSelectors } from '../devices/devices.selectors';

import {
  ChartExtremes,
  FeaturesChartsDetails,
  LoadingStatus,
  RemoteServiceDiagnosisChartLoadingStatus,
  RemoteServiceDiagnosisLoadingStatus,
  RemoteServiceFeaturesLoadingStatus,
  RemoteServiceResponseErrorType,
  RemoteServiceState,
  ThresholdsLoadingStatus,
} from './remote-service.model';
import { RemoteServiceMapper } from './remote-service.mapper';

export namespace RemoteServiceSelectors {
  export const DIAGNOSIS_ANOMALY = '1';

  export const remoteServiceState = (state: AppState): RemoteServiceState => state.remoteService;

  export function getChartDataStatus(): MemoizedSelector<
    AppState,
    LoadingStatus,
    DefaultProjectorFn<LoadingStatus>
  > {
    return createSelector(remoteServiceState, (state: RemoteServiceState) => state.chartDataStatus);
  }

  export function getDiagnosisStatus(): MemoizedSelector<
    AppState,
    LoadingStatus,
    DefaultProjectorFn<LoadingStatus>
  > {
    return createSelector(remoteServiceState, (state: RemoteServiceState) => state.diagnosisStatus);
  }

  export function getThresholdsStatus(): MemoizedSelector<
    AppState,
    ThresholdsLoadingStatus,
    DefaultProjectorFn<ThresholdsLoadingStatus>
  > {
    return createSelector(
      remoteServiceState,
      (state: RemoteServiceState) => state.thresholdsStatus
    );
  }

  export function getChartsFeatures(): MemoizedSelector<
    AppState,
    RemoteServiceFeatures,
    DefaultProjectorFn<RemoteServiceFeatures>
  > {
    return createSelector(remoteServiceState, (state: RemoteServiceState) => state.chartsFeatures);
  }

  export function getSelectedDiagnosisEntry(): MemoizedSelector<
    AppState,
    RemoteServiceDiagnosisEntry,
    DefaultProjectorFn<RemoteServiceDiagnosisEntry>
  > {
    return createSelector(remoteServiceState, (state: RemoteServiceState) =>
      state.diagnosisData.find((entry) => entry.selected)
    );
  }

  export function getDiagnosisData(): MemoizedSelector<
    AppState,
    RemoteServiceDiagnosisEntry[],
    DefaultProjectorFn<RemoteServiceDiagnosisEntry[]>
  > {
    return createSelector(remoteServiceState, (state: RemoteServiceState) => state.diagnosisData);
  }

  export function getInstallationRecommendation(
    boilerSerial: string
  ): MemoizedSelector<AppState, InstallationRecommendation, DefaultProjectorFn<any>> {
    return createSelector(
      remoteServiceState,
      (state: RemoteServiceState): InstallationRecommendation => {
        const summaryData = state.allSummaryData
          .filter((entry) => entry.data.length > 0)
          .map((entry) => {
            const groupScores: { [key: string]: number } =
              entry.data[0].recommendation_value.group_scores;

            let highestRiskValue = 0;
            for (const [_key, value] of Object.entries(groupScores)) {
              if (value > highestRiskValue) {
                highestRiskValue = value;
              }
            }

            return {
              highestRiskValue,
              boilerSerial: entry.boiler_id,
            };
          });

        const installationRecommendation = summaryData.find(
          (entry) => entry.boilerSerial === boilerSerial
        );

        return installationRecommendation;
      }
    );
  }

  export function getChartsConfig(): MemoizedSelector<
    AppState,
    RemoteServiceChartConfig[],
    DefaultProjectorFn<RemoteServiceChartConfig[]>
  > {
    return createSelector(remoteServiceState, (state: RemoteServiceState) => state.chartsConfig);
  }

  export function getChartTimestamps(): MemoizedSelector<
    AppState,
    { pointStart: number; pointInterval: number },
    DefaultProjectorFn<{ pointStart: number; pointInterval: number }>
  > {
    return createSelector(remoteServiceState, (state: RemoteServiceState) => ({
      pointStart: state.chartsFeatures?.point_start,
      pointInterval: state.chartsFeatures?.point_interval,
    }));
  }

  export function getChartExtremes(): MemoizedSelector<
    AppState,
    ChartExtremes,
    DefaultProjectorFn<ChartExtremes>
  > {
    return createSelector(remoteServiceState, (state: RemoteServiceState) => ({
      startPointMs: state.startPointMs,
      endPointMs: state.endPointMs,
    }));
  }

  export const getIsDataNotFoundError: MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > = createSelector(
    remoteServiceState,
    (state: RemoteServiceState) =>
      state?.error?.errorType === RemoteServiceResponseErrorType.DataNotFound
  );

  export const getIsDeviceNotFoundError: MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > = createSelector(
    remoteServiceState,
    (state: RemoteServiceState) =>
      state?.error?.errorType === RemoteServiceResponseErrorType.DeviceNotFound
  );

  export const getIsInvalidTimerangeError: MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > = createSelector(
    remoteServiceState,
    (state: RemoteServiceState) =>
      state?.error?.errorType === RemoteServiceResponseErrorType.InvalidTimerange
  );

  export function getAnnualReportState(): MemoizedSelector<
    AppState,
    AnnualReportData,
    DefaultProjectorFn<AnnualReportData>
  > {
    return createSelector(
      remoteServiceState,
      (state: RemoteServiceState) => state.annualReportData
    );
  }

  export const selectGetDiagnosisListData = createSelector(
    remoteServiceState,
    (state: RemoteServiceState): RemoteServiceDiagnosisEntry[] => {
      const data = state.diagnosisData;
      if (!data.length) {
        return [];
      }

      return RemoteServiceMapper.sortDiagnosisTree(data);
    }
  );

  export const selectGetAnomalyListData = createSelector(
    remoteServiceState,
    (state: RemoteServiceState): RemoteServiceDiagnosisEntry[] => {
      if (!state.diagnosisData.length) {
        return [];
      }

      const filtered = state.diagnosisData.filter(
        (entry) =>
          entry.diagnosis_anomaly === DIAGNOSIS_ANOMALY ||
          !!entry?.meta_information?.parent_diagnosis
      );

      return RemoteServiceMapper.sortDiagnosisTree(filtered);
    }
  );

  export const getRemoteServiceDiagnosisLoadingStatus: MemoizedSelector<
    AppState,
    RemoteServiceDiagnosisLoadingStatus,
    DefaultProjectorFn<RemoteServiceDiagnosisLoadingStatus>
  > = createSelector(
    remoteServiceState,
    (state: RemoteServiceState): RemoteServiceDiagnosisLoadingStatus => {
      const diagnosisDataPresent = state.diagnosisStatus.dataPresent;
      const isLoading = state.diagnosisStatus.loading;
      const isStatusServiceNotAvailable =
        !state.diagnosisStatus.dataPresent &&
        !state.diagnosisStatus.loaded &&
        !state.diagnosisStatus.loading;
      const emptySuccessData =
        state.diagnosisData.length === 0 &&
        state.diagnosisStatus.loaded &&
        !state.diagnosisStatus.loading;

      if (isLoading) {
        return RemoteServiceDiagnosisLoadingStatus.DataLoading;
      } else if (emptySuccessData) {
        return RemoteServiceDiagnosisLoadingStatus.EmptySuccessData;
      } else if (diagnosisDataPresent) {
        return RemoteServiceDiagnosisLoadingStatus.DataPresent;
      } else if (isStatusServiceNotAvailable) {
        return RemoteServiceDiagnosisLoadingStatus.ServiceNotAvailable;
      }
    }
  );

  export const remoteServiceDiagnosisChartLoadingStatus: MemoizedSelector<
    AppState,
    RemoteServiceDiagnosisChartLoadingStatus,
    DefaultProjectorFn<RemoteServiceDiagnosisChartLoadingStatus>
  > = createSelector(
    remoteServiceState,
    (state: RemoteServiceState): RemoteServiceDiagnosisChartLoadingStatus => {
      const diagnosisLoaded = state.diagnosisStatus.loaded;
      const chartDataLoaded = state.chartDataStatus.loaded;
      const dataLoadingDiagnosis = state.diagnosisStatus.loading;
      const dataLoadingCharts = state.chartDataStatus.loading;

      const dataPresent = diagnosisLoaded && chartDataLoaded;
      const isLoading = dataLoadingDiagnosis || dataLoadingCharts;

      if (isLoading) {
        return RemoteServiceDiagnosisChartLoadingStatus.DataLoading;
      } else if (dataPresent) {
        return RemoteServiceDiagnosisChartLoadingStatus.DataPresent;
      } else {
        return RemoteServiceDiagnosisChartLoadingStatus.NoData;
      }
    }
  );

  export const getRemoteServiceFeaturesLoadingStatus: MemoizedSelector<
    AppState,
    RemoteServiceFeaturesLoadingStatus,
    DefaultProjectorFn<RemoteServiceFeaturesLoadingStatus>
  > = createSelector(
    remoteServiceState,
    getIsDataNotFoundError,
    getIsInvalidTimerangeError,
    (
      state: RemoteServiceState,
      isDataNotFoundError,
      isInvalidTimerangeError
    ): RemoteServiceFeaturesLoadingStatus => {
      const noFeaturesData = !state.chartDataStatus.dataPresent;
      const featuresNotLoading = !state.chartDataStatus.loading;
      const featuresRequestFailed = !state.chartDataStatus.loaded;

      // Data is present but does contain only features which are not supported
      const isStatusOnlyNotSupportedFeatures =
        state.chartDataStatus.dataPresent &&
        !state.chartsFeatures.series.some((entry) => entry.feature.includes('adf'));
      // Data is present and constains supported features
      const isStatusDataPresent = state.chartDataStatus.dataPresent;
      // Data not found for period of time
      const isStatusNoData = noFeaturesData && featuresNotLoading && isDataNotFoundError;
      // No data found in the backend
      const isStatusServiceNotAvailable =
        noFeaturesData && featuresNotLoading && featuresRequestFailed;

      if (state.chartDataStatus.loading) {
        return RemoteServiceFeaturesLoadingStatus.DataLoading;
      } else if (isStatusOnlyNotSupportedFeatures) {
        return RemoteServiceFeaturesLoadingStatus.DataWithNotSupportedFeatures;
      } else if (isStatusDataPresent) {
        return RemoteServiceFeaturesLoadingStatus.DataPresent;
      } else if (isStatusNoData || isInvalidTimerangeError) {
        return RemoteServiceFeaturesLoadingStatus.NoData;
      } else if (isStatusServiceNotAvailable) {
        return RemoteServiceFeaturesLoadingStatus.ServiceNotAvailable;
      } else {
        return RemoteServiceFeaturesLoadingStatus.DataLoading;
      }
    }
  );

  export const getCanDisplayNoAnomaliesInformation: MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > = createSelector(
    remoteServiceState,
    selectGetAnomalyListData,
    (state: RemoteServiceState, anomalies) => {
      const noAnomalies = anomalies?.length > 0;
      const requestSuccess = state.diagnosisStatus.loaded;
      const chartDataPresent = state.chartDataStatus.dataPresent;

      return noAnomalies && requestSuccess && chartDataPresent;
    }
  );

  export const getFeaturesChartsDetails: MemoizedSelector<
    AppState,
    FeaturesChartsDetails,
    DefaultProjectorFn<FeaturesChartsDetails>
  > = createSelector(remoteServiceState, (state: RemoteServiceState) => {
    const details: FeaturesChartsDetails = {
      chartsConfig: state.chartsConfig,
      chartTimestamps: {
        pointStart: state.chartsFeatures?.point_start,
        pointInterval: state.chartsFeatures?.point_interval,
      },
      chartExtremes: { startPointMs: state.startPointMs, endPointMs: state.endPointMs },
    };

    return details;
  });

  export const getFeaturesChartSeries = createSelector(
    remoteServiceState,
    (state: RemoteServiceState): RemoteServiceFeaturesChartSeries[] => {
      const chartsFeatures = state.chartsFeatures;

      if (!chartsFeatures?.series?.length) {
        return [];
      }
      const chartsConfigsFeatures = state?.chartsConfig?.map((entry) => {
        return {
          seriesNameKey: entry.title,
          features: entry.features,
        };
      });

      const chartSeries: RemoteServiceFeaturesChartSeries[] = [];
      chartsConfigsFeatures?.forEach((config) => {
        const validSeries = chartsFeatures.series.filter((series) =>
          config.features.includes(series.feature)
        );

        if (validSeries.length > 0) {
          chartSeries.push({ series: validSeries, seriesNameKey: config.seriesNameKey });
        }
      });

      return chartSeries;
    }
  );

  export const ignoredRemoteServiceNotifications = createSelector(
    remoteServiceState,
    (state: RemoteServiceState): string[] => state.ignoredRemoteServiceNotifications ?? []
  );

  export const isLowBudgetNotificationVisible = createSelector(
    AuthSelectors.isRsaOrMaintainer,
    DevicesSelectors.isLowBudgetDevice,
    ignoredRemoteServiceNotifications,
    DevicesSelectors.getSelectedDevice,
    (
      isRsaOrMaintainer: boolean,
      isLowBudget: boolean,
      ignoredNotifications: string[],
      selectedDevice
    ): boolean => {
      const gatewaySerial = selectedDevice?.gatewaySerial;

      if (gatewaySerial) {
        return isRsaOrMaintainer && isLowBudget && !ignoredNotifications.includes(gatewaySerial);
      } else {
        return false;
      }
    }
  );

  export const selectDiagnosisCalculationDataStatus: MemoizedSelector<
    AppState,
    RemoteServiceDiagnosisLoadingStatus,
    DefaultProjectorFn<RemoteServiceDiagnosisLoadingStatus>
  > = createSelector(remoteServiceState, (state: RemoteServiceState) => {
    const isDiagnosisCalculationsNotReadyYet =
      state?.diagnosisStatus?.isGwWithIdDoesNotExist || !state?.diagnosisStatus?.dataPresent;

    return isDiagnosisCalculationsNotReadyYet
      ? RemoteServiceDiagnosisLoadingStatus.DiagnosisCalculationsNotReadyYet
      : RemoteServiceDiagnosisLoadingStatus.DataPresent;
  });

  export const selectSelectedDevice = createSelector(
    remoteServiceState,
    (state: RemoteServiceState): RichDevice => {
      return state.selectedDevice;
    }
  );
}
